import { Dialog, type DialogProps, type ModalDialogButtonSetting } from '@myosh/odin-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserActionTypes, useUser } from '../context/user.context';
import { reloadPage } from '../helpers/reload-page';
import { ControlledUserPicker } from './controlled-user-picker.component';

export interface ControlledUserSelectDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** Called when the dialog requests to open or close (overlay, close icon, Esc, or confirm). */
  onOpenChange: (open: boolean) => void;
  /** Runs before the controlled username is updated, while API requests still act as the previous controlled user. Use for session teardown that must happen under the old identity. */
  onBeforeChange?: (username: string | null) => void | Promise<void>;
  /** Overrides the refresh after the controlled username is updated. Defaults to a full page reload. An override must still force one (e.g. `window.location.replace('/')`) so no state from the previous user survives. */
  onChanged?: (username: string | null) => void;
}

type ControlledUserSelectDialogAllProps = ControlledUserSelectDialogProps &
  Omit<DialogProps, 'visible' | 'onClose' | 'buttons' | 'children'>;

/**
 * Controlled-user switcher for superusers. Owns its staged selection (seeded from the user
 * context's `controlledUsername` and reset each time it opens) and updates the user context
 * itself on confirm. App-specific apply logic hangs off `onBeforeChange` and `onChanged`.
 *
 * Both the embedded `ControlledUserPicker` and the confirm handler read the user context, so the
 * dialog must render inside a `UserProvider` with an authenticated session.
 */
export const ControlledUserSelectDialog = ({ open, ...props }: ControlledUserSelectDialogAllProps) => {
  // Keying by `open` remounts the content on each open, which reinitialises the staged selection
  // so the dialog re-seeds from the current controlled username every time it opens, including
  // when the parent closes it by toggling `open` directly instead of via onClose.
  return <ControlledUserSelectDialogContent key={String(open)} open={open} {...props} />;
};

const ControlledUserSelectDialogContent = ({
  open,
  onOpenChange,
  onBeforeChange,
  onChanged,
  ...props
}: ControlledUserSelectDialogAllProps) => {
  const { t } = useTranslation();
  const { state, dispatch } = useUser();
  const currentUser = state.user?.controlledUsername ?? null;
  const [staged, setStaged] = useState<string | null>(currentUser);

  // Confirming is strictly a switch: it needs a picked user that differs from the current one.
  // Stopping is only offered through the dedicated button, not by clearing the picker.
  const canConfirm = staged !== null && staged !== currentUser;

  const applyChange = async (username: string | null) => {
    await onBeforeChange?.(username);
    dispatch({ type: UserActionTypes.UpdateControlledUsername, payload: username ?? undefined });
    onOpenChange(false);
    if (onChanged) {
      onChanged(username);
    } else {
      reloadPage();
    }
  };

  const confirmButton: ModalDialogButtonSetting = {
    name: 'confirm-user',
    text: t('change-user'),
    disabled: !canConfirm,
    onClick: () => void applyChange(staged),
  };

  const stopControllingButton: ModalDialogButtonSetting = {
    name: 'stop-controlling',
    text: t('stop-controlling'),
    variant: 'outline',
    onClick: () => void applyChange(null),
  };

  return (
    <Dialog
      header={t('select-user')}
      visible={open}
      onClose={() => onOpenChange(false)}
      containerClassName="sm:w-96"
      buttons={currentUser ? [stopControllingButton, confirmButton] : [confirmButton]}
      {...props}
    >
      {/* The native dialog keeps closed children in the DOM, so gate the picker on `open` to avoid fetching users before the dialog is shown. */}
      {open && <ControlledUserPicker value={staged} onValueChange={setStaged} />}
    </Dialog>
  );
};
